// DAILY EXHAUSTIN — one puzzle a day, one attempt, and a streak to protect.
//
// THE PUZZLE IS DERIVED FROM THE DATE, not stored and not random. Two things
// follow, and both of them are the point:
//
//   · it is the same puzzle all day, so closing the app and reopening it
//     cannot re-roll a bad draw;
//   · it needs no server, which is what lets the Daily work on a plane.
//
// A stored "today's puzzle" row would have given the first property and not
// the second, and would have needed a migration the first time the bank grew.

import { drawQuickfire, toQuickfire } from './quickfire.js';
import { BANK } from './bank/index.js';
import { ALL_TOPIC_IDS } from './topics.js';
import { MODES } from './modes.js';

/** Local date, not UTC — his "today" is the one where he is standing. */
export function todayKey(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function yesterdayKey(d = new Date()) {
  const y = new Date(d);
  y.setDate(y.getDate() - 1);
  return todayKey(y);
}

/** FNV-1a. Small, fast, and stable across every browser — which is what matters. */
function hash(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** mulberry32 — a seeded PRNG, so "random" is reproducible from the date. */
function seeded(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const DAILY_LENGTH = 5;

// Sort It is excluded for the same reason Endurance excludes it: the Daily is
// one attempt, and losing your only go of the day to a dragging interaction
// rather than to a fact you did not know is the worst possible version of
// "come back tomorrow".
const DAILY_MODES = ['last-gasp', 'higher-lower', 'odd-one-out', 'guess-the-thing', 'chain-reaction'];

/**
 * Today's puzzle: one mode, five questions, identical for the whole day.
 *
 * Note what is and is not deterministic. WHICH mode and WHICH five questions
 * come from the date, so the puzzle is the same all day. The ORDER of the
 * options within each question does not — `toQuickfire` shuffles them — and
 * that is fine, because he only gets one attempt anyway.
 *
 * @returns {{ dateKey:string, mode:string, modeName:string, questions:object[] }}
 */
export function dailyPlan(dateKey = todayKey()) {
  const rng = seeded(hash(`exhaustin:${dateKey}`));
  const mode = DAILY_MODES[Math.floor(rng() * DAILY_MODES.length)];

  const pool = BANK[mode] ?? [];
  // A seeded ordering rather than engine.shuffle(), which uses Math.random and
  // would give a different five questions on every render of the same day.
  const ordered = [...pool].sort((a, b) => hash(`${dateKey}:${a.id}`) - hash(`${dateKey}:${b.id}`));
  const questions = ordered.slice(0, DAILY_LENGTH).map(toQuickfire).filter(Boolean);

  // Should never happen with the shipped bank, but a Daily that renders nothing
  // is a dead screen rather than a visible error, so fall back to a live draw.
  const safe = questions.length
    ? questions
    : drawQuickfire({ topics: ALL_TOPIC_IDS, difficulty: 'warm-up', count: DAILY_LENGTH });

  return {
    dateKey,
    mode,
    modeName: MODES.find((m) => m.id === mode)?.name ?? mode,
    questions: safe.slice(0, DAILY_LENGTH),
  };
}

/** Getting 3 of 5 keeps the streak. Perfection is not the price of coming back. */
export const DAILY_PASS_MARK = 3;

/**
 * What today's result does to the streak.
 *
 * Played yesterday → it continues. Missed a day → it restarts at 1 rather than
 * going to zero, because a streak counter that reads 0 the day you came back
 * is a reason not to come back again.
 */
export function nextStreak({ lastDailyOn, dailyStreak = 0, survived }) {
  if (!survived) return 0;
  if (lastDailyOn === yesterdayKey()) return (dailyStreak ?? 0) + 1;
  if (lastDailyOn === todayKey()) return dailyStreak ?? 1;
  return 1;
}

export const playedToday = (lastDailyOn) => lastDailyOn === todayKey();
